// 訂單管理頁（含刪除功能）- Material UI 版本
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  TableSortLabel,
  useMediaQuery,
  useTheme,
  Checkbox,
  TablePagination,
  InputLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  Notifications as NotificationIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import ManualRegistrationDialog from '../../components/ManualRegistrationDialog';

// 從 API 載入的選項列表（將在組件內動態載入）
let fieldOptions = {
  community: [],
  department: [],
  position: []
};

export default function OrderAdminPage({ productId, productName }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 取得當前使用者角色
  const storedUser = localStorage.getItem('user');
  const currentRole = storedUser ? JSON.parse(storedUser).role.name : null;

  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [communityFilter, setCommunityFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, orderId: null });
  const [notificationDialog, setNotificationDialog] = useState({ open: false });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [notificationForm, setNotificationForm] = useState({ message: '', sendAll: false });
  const [basicDataOptions, setBasicDataOptions] = useState({
    community: [],
    department: [],
    position: []
  });
  
  // 分頁狀態
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // 載入基本檔資料選項
  const fetchBasicDataOptions = async () => {
    try {
      const dataTypes = ['community', 'department', 'position'];
      const promises = dataTypes.map(async (type) => {
        const response = await fetch(`/api/basic-data/${type}`, { credentials: 'include' });
        const data = await response.json();
        return { type, data };
      });
      
      const results = await Promise.all(promises);
      const newOptions = {};
      results.forEach(({ type, data }) => {
        newOptions[type] = data;
      });
      
      setBasicDataOptions(newOptions);
      
      // 更新全局 fieldOptions 以供其他地方使用
      fieldOptions.community = newOptions.community;
      fieldOptions.department = newOptions.department;
      fieldOptions.position = newOptions.position;
    } catch (error) {
      setSnackbar({ open: true, message: '載入基本檔資料失敗', severity: 'error' });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (productId) {
        params.append('productId', productId);
      }
      
      const url = '/api/orders' + (params.toString() ? `?${params.toString()}` : '');
      const response = await fetch(url, { credentials: 'include' });
      const result = await response.json();
      
      const orders = Array.isArray(result) ? result : [];
      const mappedOrders = orders.map(o => ({ ...o, key: o.id }));
      setOriginalData(mappedOrders);
      setData(mappedOrders);
    } catch (error) {
      setSnackbar({ open: true, message: '載入失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 社團選項（依目前活動的報名資料統計人數，人多的排前面）
  const NO_COMMUNITY = '__none__';
  const communityOptions = useMemo(() => {
    const counts = {};
    originalData.forEach(order => {
      const c = (order.extra?.community || '').trim() || NO_COMMUNITY;
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [originalData]);

  // 搜尋關鍵字 + 社團篩選一起套用
  const applyFilters = (query, community, resetPage = true) => {
    const searchTerm = query.trim().toLowerCase();
    setData(originalData.filter(order => {
      if (community) {
        const c = (order.extra?.community || '').trim() || NO_COMMUNITY;
        if (c !== community) return false;
      }
      if (!searchTerm) return true;
      const userName = order.user?.name || '';
      const userDisplayName = order.user?.displayName || '';
      const productNames = order.orderItems?.map(item => item.product?.name || '').join(' ') || '';
      const extraValues = order.extra ? Object.values(order.extra).join(' ') : '';
      
      const searchableText = `${userName} ${userDisplayName} ${productNames} ${extraValues}`.toLowerCase();
      
      return searchableText.includes(searchTerm);
    }));
    if (resetPage) setPage(0);
  };

  // 重新載入資料（例如編輯後）時保留目前的搜尋與社團篩選
  useEffect(() => {
    applyFilters(searchQuery, communityFilter, false);
  }, [originalData]);

  // 即時搜尋過濾功能
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    applyFilters(e.target.value, communityFilter);
  };

  const handleCommunityChange = (e) => {
    setCommunityFilter(e.target.value);
    applyFilters(searchQuery, e.target.value);
  };

  // 清除搜尋
  const handleClearSearch = () => {
    setSearchQuery('');
    setCommunityFilter('');
    setData(originalData);
    setPage(0);
  };

  // 排序功能
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
    
    const sortedData = [...data].sort((a, b) => {
      let aValue, bValue;
      
      if (key === 'user') {
        aValue = a.user?.name || '';
        bValue = b.user?.name || '';
      } else if (key === 'event') {
        aValue = a.orderItems?.[0]?.product?.name || '';
        bValue = b.orderItems?.[0]?.product?.name || '';
      } else if (key === 'createdAt') {
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        aValue = a.extra?.[key] || '';
        bValue = b.extra?.[key] || '';
      }
      
      return direction === 'asc' 
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
    
    setData(sortedData);
  };

  useEffect(() => {
    fetchBasicDataOptions();
    fetchData();
  }, [productId]);

  // 編輯處理函數
  const handleEdit = (order) => {
    setEditingOrder(order.id);
    setEditFormData({
      community: order.extra?.community || '',
      department: order.extra?.department || '',
      position: order.extra?.position || '',
      name: order.extra?.name || '',
      mobile: order.extra?.mobile || '',
      other: order.extra?.other || ''
    });
  };

  const handleEditCancel = () => {
    setEditingOrder(null);
    setEditFormData({});
  };

  const handleEditSave = async (orderId) => {
    try {
      const order = data.find(o => o.id === orderId);
      const updatedExtra = { ...order.extra, ...editFormData };
      
      const response = await fetch(`/api/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ extra: updatedExtra })
      });

      if (!response.ok) throw new Error('更新失敗');

      setSnackbar({ open: true, message: '更新成功', severity: 'success' });
      setEditingOrder(null);
      setEditFormData({});
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: '更新失敗: ' + error.message, severity: 'error' });
    }
  };

  const handleDelete = async (orderId) => {
    try {
      const response = await fetch(`/api/orders/${orderId}`, { 
        method: 'DELETE', 
        credentials: 'include' 
      });
      if (!response.ok) throw new Error('刪除失敗');
      
      setSnackbar({ open: true, message: '刪除成功', severity: 'success' });
      setDeleteDialog({ open: false, orderId: null });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    }
  };

  const handleResendTicket = async (orderId, userName) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/resend-ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSnackbar({ open: true, message: `已成功重新發送電子票給 ${userName}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: `重新發送失敗: ${result.error}`, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '系統錯誤，請稍後再試', severity: 'error' });
    }
  };

  // 匯出 Excel：只匯出目前畫面篩選後的資料（全部／某社團／搜尋結果）
  const handleExport = async () => {
    if (!data.length) {
      setSnackbar({ open: true, message: '目前沒有可匯出的資料', severity: 'warning' });
      return;
    }
    const isFiltered = data.length !== originalData.length;
    const communityLabel = communityFilter === NO_COMMUNITY ? '未填社團' : communityFilter;
    const filename = [productName || '報名資料', communityLabel || (isFiltered ? '篩選結果' : '全部'), new Date().toLocaleDateString('sv-SE')]
      .filter(Boolean).join('_');
    setExporting(true);
    try {
      const response = await fetch('/api/orders/export', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, ids: isFiltered ? data.map(o => o.id) : undefined, filename })
      });
      if (!response.ok) throw new Error('匯出失敗');
      const blob = await response.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // 通知功能
  const handleNotificationOpen = () => {
    setNotificationDialog({ open: true });
    setNotificationForm({ message: '', sendAll: false });
  };

  const handleNotificationSend = async () => {
    try {
      const { message: notificationMessage, sendAll } = notificationForm;

      if (!notificationMessage.trim()) {
        setSnackbar({ open: true, message: '請輸入通知訊息', severity: 'warning' });
        return;
      }

      let targetUsers = [];
      if (sendAll) {
        targetUsers = data.filter(order => order.user?.lineId).map(order => ({
          lineId: order.user.lineId,
          name: order.user.name || order.user.displayName
        }));
        // 去重複
        const uniqueUsers = targetUsers.reduce((acc, user) => {
          if (!acc.find(u => u.lineId === user.lineId)) {
            acc.push(user);
          }
          return acc;
        }, []);
        targetUsers = uniqueUsers;
      } else {
        targetUsers = selectedOrders.map(orderId => {
          const order = data.find(o => o.id === orderId);
          return {
            lineId: order.user?.lineId,
            name: order.user?.name || order.user?.displayName
          };
        }).filter(user => user.lineId);
      }

      if (targetUsers.length === 0) {
        setSnackbar({ open: true, message: '沒有可發送的用戶', severity: 'warning' });
        return;
      }

      // 批量發送通知
      const promises = targetUsers.map(user => 
        fetch('/api/notifications', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            lineId: user.lineId,
            channel: 'LINE',
            address: user.lineId,
            message: notificationMessage
          })
        })
      );

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        setSnackbar({ 
          open: true, 
          message: `成功發送 ${successCount} 則通知${failCount > 0 ? `，失敗 ${failCount} 則` : ''}`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ open: true, message: '通知發送失敗', severity: 'error' });
      }

      setNotificationDialog({ open: false });
      setSelectedOrders([]);
      setNotificationForm({ message: '', sendAll: false });

    } catch (error) {
      setSnackbar({ open: true, message: '發送通知失敗，請稍後再試', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 處理選擇
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = data.filter(order => order.user?.lineId).map(order => order.id);
      setSelectedOrders(newSelected);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOne = (orderId) => {
    const selectedIndex = selectedOrders.indexOf(orderId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedOrders, orderId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedOrders.slice(1));
    } else if (selectedIndex === selectedOrders.length - 1) {
      newSelected = newSelected.concat(selectedOrders.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedOrders.slice(0, selectedIndex),
        selectedOrders.slice(selectedIndex + 1),
      );
    }

    setSelectedOrders(newSelected);
  };

  const EditableCell = ({ order, field, type = 'text' }) => {
    const isEditing = editingOrder === order.id;
    
    if (!isEditing) {
      return order.extra?.[field] || '';
    }

    if (type === 'select') {
      return (
        <FormControl size="small" fullWidth>
          <Select
            value={editFormData[field] || ''}
            onChange={(e) => setEditFormData({...editFormData, [field]: e.target.value})}
          >
            {fieldOptions[field]?.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        size="small"
        fullWidth
        value={editFormData[field] || ''}
        onChange={(e) => setEditFormData({...editFormData, [field]: e.target.value})}
        variant="outlined"
      />
    );
  };

  const isSelected = (id) => selectedOrders.indexOf(id) !== -1;
  const selectableOrders = data.filter(order => order.user?.lineId);
  const numSelected = selectedOrders.length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  // 分頁後的數據
  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
        {productName ? `${productName} - 報名管理` : '報名管理'}
      </Typography>
      
      {/* 搜尋區域 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, alignItems: { md: 'center' } }}>
          <TextField
            sx={{ flex: 1 }}
            label="搜尋報名資料"
            placeholder="搜尋使用者、活動名稱、社團、社別、社職、姓名、手機..."
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: { md: 240 } }}>
            <InputLabel id="community-filter-label">社團</InputLabel>
            <Select
              labelId="community-filter-label"
              label="社團"
              value={communityFilter}
              onChange={handleCommunityChange}
            >
              <MenuItem value="">全部（{originalData.length}）</MenuItem>
              {communityOptions.map(([c, n]) => (
                <MenuItem key={c} value={c}>{c === NO_COMMUNITY ? '（未填社團）' : c}（{n}）</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {productId && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PersonAddIcon />}
                onClick={() => setManualOpen(true)}
                size="small"
                sx={{ flex: { xs: '1 1 100%', md: 'none' }, whiteSpace: 'nowrap' }}
              >
                手動報名
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearSearch}
              size="small"
              sx={{ flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap' }}
            >
              清除
            </Button>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting || loading}
              size="small"
              sx={{ flex: { xs: 2, md: 'none' }, whiteSpace: 'nowrap' }}
            >
              下載 Excel（{data.length} 筆）
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* 通知操作區域 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<NotificationIcon />}
            onClick={handleNotificationOpen}
          >
            發送通知
          </Button>
          <Typography variant="body2" color="text.secondary">
            {selectedOrders.length > 0 
              ? `已選擇 ${selectedOrders.length} 個用戶` 
              : '請選擇用戶或在彈窗中選擇全部發送'
            }
          </Typography>
        </Box>
      </Paper>
      
      <TableContainer component={Paper} elevation={1}>
        <Table size={isMobile ? 'small' : 'medium'} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < selectableOrders.length}
                  checked={selectableOrders.length > 0 && numSelected === selectableOrders.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell align="center">序號</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'user'}
                  direction={sortConfig.key === 'user' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('user')}
                >
                  使用者
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'event'}
                  direction={sortConfig.key === 'event' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('event')}
                >
                  活動名稱
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'community'}
                      direction={sortConfig.key === 'community' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('community')}
                    >
                      社團
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'department'}
                      direction={sortConfig.key === 'department' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('department')}
                    >
                      社別/會別
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'position'}
                      direction={sortConfig.key === 'position' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('position')}
                    >
                      社職/會職
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'name'}
                      direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      姓名
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'mobile'}
                      direction={sortConfig.key === 'mobile' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('mobile')}
                    >
                      手機
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>其它</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'createdAt'}
                      direction={sortConfig.key === 'createdAt' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      建立時間
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((order, index) => {
              const isEditing = editingOrder === order.id;
              const isOrderSelected = isSelected(order.id);
              const globalIndex = page * rowsPerPage + index + 1;
              
              return (
                <TableRow key={order.id} hover selected={isOrderSelected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isOrderSelected}
                      onChange={() => handleSelectOne(order.id)}
                      disabled={!order.user?.lineId}
                    />
                  </TableCell>
                  <TableCell align="center">{globalIndex}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={order.user?.pictureUrl} sx={{ width: 24, height: 24 }} />
                      <Typography variant="body2">
                        {order.user?.name || order.user?.displayName || ''}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.orderItems?.[0]?.product?.name || ''}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell>
                        <EditableCell order={order} field="community" type="select" />
                      </TableCell>
                      <TableCell>
                        <EditableCell order={order} field="department" type="select" />
                      </TableCell>
                      <TableCell>
                        <EditableCell order={order} field="position" type="select" />
                      </TableCell>
                      <TableCell>
                        <EditableCell order={order} field="name" />
                      </TableCell>
                      <TableCell>
                        <EditableCell order={order} field="mobile" />
                      </TableCell>
                      <TableCell>
                        <EditableCell order={order} field="other" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {isEditing ? (
                        <>
                          <Tooltip title="保存">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditSave(order.id)}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="取消">
                            <IconButton
                              size="small"
                              onClick={handleEditCancel}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="編輯">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={editingOrder !== null}
                              onClick={() => handleEdit(order)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={order.user?.lineId ? '重新發送電子票' : '該用戶沒有 LINE ID'}>
                            <span>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleResendTicket(order.id, order.user?.name)}
                                disabled={!order.user?.lineId}
                              >
                                <SendIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="刪除">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, orderId: order.id })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={isMobile ? 5 : 12} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? '未找到符合條件的報名記錄' : '暫無報名資料'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[50, 100, 200, 500]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage="每頁顯示："
        labelDisplayedRows={({ from, to, count }) => `第 ${from}-${to} 項，共 ${count} 項`}
      />

      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, orderId: null })}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除此報名記錄嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, orderId: null })}>
            取消
          </Button>
          <Button 
            onClick={() => handleDelete(deleteDialog.orderId)}
            color="error"
            variant="contained"
          >
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知對話框 */}
      <Dialog
        open={notificationDialog.open}
        onClose={() => setNotificationDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>發送通知給報名用戶</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <Checkbox
                checked={notificationForm.sendAll}
                onChange={(e) => setNotificationForm({...notificationForm, sendAll: e.target.checked})}
              />
              <Typography variant="body2" component="span">
                發送給所有報名用戶（覆蓋下方的特定選擇）
              </Typography>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="通知訊息"
              placeholder="請輸入要發送的通知訊息..."
              value={notificationForm.message}
              onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
              variant="outlined"
              inputProps={{ maxLength: 500 }}
              helperText={`${notificationForm.message.length}/500`}
              sx={{ mb: 2 }}
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>發送說明：</strong><br />
                • 勾選「發送給所有報名用戶」將發送給所有擁有LINE ID的報名用戶<br />
                • 不勾選時，將發送給您在表格中選中的用戶<br />
                • 只有擁有LINE ID的用戶才能接收通知<br />
                • 系統將自動去重複用戶
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialog({ open: false })}>
            取消
          </Button>
          <Button 
            onClick={handleNotificationSend}
            color="primary"
            variant="contained"
            startIcon={<NotificationIcon />}
          >
            發送通知
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {productId && (
        <ManualRegistrationDialog
          open={manualOpen}
          onClose={() => setManualOpen(false)}
          productId={productId}
          productName={productName}
          options={basicDataOptions}
          onCreated={(result, name) => {
            setSnackbar({
              open: true,
              severity: 'success',
              message: `已為「${name}」完成報名（編號 ${result.orderId}）${result.ticketSent ? '，已推送 LINE 電子票' : ''}`
            });
            fetchData();
          }}
        />
      )}
    </Box>
  );
}